import { Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FormRuntimeRendererComponent } from '../../shared/dynamic-form/form-runtime-renderer.component';
import { FormRuntimeProgressAdapter } from '../../shared/dynamic-form/form-runtime-progress.contracts';
import { OrganizationDynamicApiService, PublicFormDescriptor } from './organization-dynamic-api.service';

@Component({
  standalone: true,
  selector: 'ain-organization-form-fill',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormRuntimeRendererComponent,
    ButtonModule,
    ToastModule,
    RouterLink
  ],
  providers: [MessageService],
  template: `
    <p-toast />
    <ain-page [title]="descriptor()?.name ?? t('organization.workflow.forms.fill')" [description]="slug">
      <a routerLink="/workflow/forms">{{ t('common.back') }}</a>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (descriptor(); as formDescriptor) {
        <ain-form-runtime-renderer
          [schemaJson]="schemaJson()"
          mode="runtime"
          [lookupSearchEnabled]="true"
          [saveResumeEnabled]="formDescriptor.saveResumeEnabled"
          [honeypotEnabled]="formDescriptor.honeypotEnabled"
          [progressAdapter]="progressAdapter()"
          [resumeBaseUrl]="resumeBaseUrl()"
          [showRuntimeInfoHint]="true"
          [submitting]="submitBusy()"
          (submit)="onSubmit($event)" />
      } @else {
        <ain-state type="error" [title]="t('common.loadFailed')" />
      }
    </ain-page>
  `,
  styles: [`
    a { display: inline-block; margin-bottom: 12px; }
  `]
})
export class OrganizationFormFillComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(OrganizationDynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  slug = '';
  readonly loading = signal(true);
  readonly submitBusy = signal(false);
  readonly descriptor = signal<PublicFormDescriptor | null>(null);
  readonly progressAdapter = signal<FormRuntimeProgressAdapter | null>(null);
  readonly runtimeRenderer = viewChild(FormRuntimeRendererComponent);

  readonly schemaJson = computed(() => {
    const descriptor = this.descriptor();
    if (!descriptor) {
      return null;
    }
    return descriptor.schemaJson?.trim() || null;
  });

  readonly resumeBaseUrl = computed(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('resume');
    return url.toString();
  });

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (!this.slug) {
      this.loading.set(false);
      return;
    }

    this.api.getPublicForm(this.slug).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: descriptor => {
        this.descriptor.set(descriptor);
        this.progressAdapter.set(this.api.createProgressAdapter(this.slug));
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  onSubmit(data: Record<string, unknown>): void {
    const descriptor = this.descriptor();
    if (!descriptor) {
      return;
    }

    this.submitBusy.set(true);
    const honeypot = this.runtimeRenderer()?.getHoneypotValue() ?? null;
    this.api.submitPublicForm(this.slug, {
      data,
      honeypot: descriptor.honeypotEnabled ? honeypot : null,
      captchaToken: null
    }).pipe(
      finalize(() => this.submitBusy.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => {
        if (response.error || response.issues?.length) {
          this.messages.add({ severity: 'error', summary: this.t('organization.workflow.forms.submitFailed') });
          return;
        }
        this.messages.add({ severity: 'success', summary: this.t('organization.workflow.forms.submitSuccess') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('organization.workflow.forms.submitFailed') })
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
